import React from "react";
import { Clock3, TrendingDown, TrendingUp } from "lucide-react";
import { Box, Card, Chip, Stack, Typography } from "@mui/material";

function clampPercent(value) {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return 0;
	return Math.max(0, Math.min(100, Math.round(numeric)));
}

function formatInt(value) {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return "0";
	return new Intl.NumberFormat("es-MX").format(Math.round(numeric));
}

function formatSecondsAsMinutesClock(value) {
	const numeric = Number(value);
	if (!Number.isFinite(numeric) || numeric < 0) return "00:00";
	const totalSeconds = Math.round(numeric);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function ProgresoGeneral({
	data,
	summary,
	dailyGoal,
	weeklyDeltaPercentage,
	trendSuffix = "%",
	trendLabel,
	trendLabelBelow = false,
	showTrendSign = true,
	title = "Tu progreso general",
}) {
	const effectiveSummary = summary || data?.summary || {};
	const effectiveDailyGoal = dailyGoal || data?.daily_goal || {};

	const accuracyPercentage = clampPercent(effectiveSummary?.accuracy_percentage);
	const totalCorrect = formatInt(effectiveSummary?.total_correct);
	const totalAnswered = formatInt(effectiveSummary?.total_answered);

	const hasWeeklyDelta = Number.isFinite(Number(weeklyDeltaPercentage));
	const trendValueRaw = hasWeeklyDelta
		? Number(weeklyDeltaPercentage)
		: Number(effectiveDailyGoal?.progress_percentage || 0);
	const trendValue = Math.round(Math.abs(trendValueRaw));
	const trendPositive = trendValueRaw >= 0;
	const effectiveTrendLabel = trendLabel || (hasWeeklyDelta ? "vs. la semana pasada" : "de tu meta diaria");
	const isTimeTrend = /tiempo.*respuesta/i.test(effectiveTrendLabel);
	const formattedTimeTrendValue = formatSecondsAsMinutesClock(trendValueRaw);

	const radius = 44;
	const strokeWidth = 11;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference * (1 - accuracyPercentage / 100);

	return (
		<Card
			elevation={0}
			sx={{
				borderRadius: "0.5rem",
				border: "1px solid",
				borderColor: "#e5e7eb",
				bgcolor: "#fff",
				boxShadow:
					"0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
				p: 2,
			}}
		>
			<Typography sx={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em", color: "#0f172a" }}>
				{title}
			</Typography>

			<Box
				sx={{
					mt: 2,
					display: "flex",
					alignItems: { xs: "center", sm: "center" },
					justifyContent: "space-between",
					gap: 2.5,
					flexDirection: { xs: "row", sm: "row" },
				}}
			>
				<Box
					sx={{
						position: "relative",
						width: { xs: 96, sm: 124, md: 138, lg: 124 },
						height: { xs: 96, sm: 124, md: 138, lg: 124 },
					}}
				>
					<svg
						style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}
						viewBox="0 0 100 100"
						role="img"
						aria-label={`Precision general ${accuracyPercentage}%`}
					>
						<circle
							cx="50"
							cy="50"
							r={radius}
							fill="none"
							stroke="#e5e7eb"
							strokeWidth={strokeWidth}
						/>
						<circle
							cx="50"
							cy="50"
							r={radius}
							fill="none"
							stroke="#2563eb"
							strokeLinecap="round"
							strokeWidth={strokeWidth}
							strokeDasharray={circumference}
							strokeDashoffset={offset}
						/>
					</svg>

					<Box
						sx={{
							position: "absolute",
							inset: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Typography sx={{ fontSize: { xs: "1.05rem", sm: "1.55rem", md: "1.3rem", lg: "1.55rem" }, fontWeight: 700, color: "#0f172a" }}>
							{accuracyPercentage}%
						</Typography>
					</Box>
				</Box>

				<Box sx={{ flex: 1, width: "100%", textAlign: "left" }}>
					<Box sx={{ mb: 1.5 }}>
						<Typography sx={{ fontSize: { xs: "0.82rem", sm: "0.9rem", md: "0.78rem", lg: "0.9rem" }, color: "#64748b" }}>
							Aciertos totales
						</Typography>
						<Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.1rem", lg: "1.5rem" }, fontWeight: 700, lineHeight: 1.15, color: "#0f172a" }}>
							{totalCorrect}
						</Typography>
					</Box>

					<Box sx={{ mb: 2 }}>
						<Typography sx={{ fontSize: { xs: "0.82rem", sm: "0.9rem", md: "0.78rem", lg: "0.9rem" }, color: "#64748b" }}>
							Preguntas realizadas
						</Typography>
						<Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.1rem", lg: "1.5rem" }, fontWeight: 700, lineHeight: 1.15, color: "#0f172a" }}>
							{totalAnswered}
						</Typography>
					</Box>

					<Chip
						label={
							trendLabelBelow ? (
								<Stack spacing={0.2} alignItems="center" sx={{ py: 0.25 }}>
									<Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
										{isTimeTrend ? <Clock3 size={14} /> : trendPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
										<Typography component="span" sx={{ fontSize: { xs: "0.72rem", sm: "0.78rem", md: "0.68rem", lg: "0.78rem" }, fontWeight: 600, lineHeight: 1 }}>
											{isTimeTrend ? "" : showTrendSign ? (trendPositive ? "+" : "-") : ""}
											{isTimeTrend ? formattedTimeTrendValue : `${trendValue}${trendSuffix}`}
										</Typography>
									</Stack>
									<Typography component="span" sx={{ fontSize: { xs: "0.68rem", sm: "0.72rem", md: "0.62rem", lg: "0.72rem" }, fontWeight: 600, lineHeight: 1.1, textAlign: "center", whiteSpace: "normal" }}>
										{effectiveTrendLabel}
									</Typography>
								</Stack>
							) : (
								<Stack direction="row" spacing={0.75} alignItems="center">
									{trendPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
									<Typography component="span" sx={{ fontSize: { xs: "0.72rem", sm: "0.78rem", md: "0.68rem", lg: "0.78rem" }, fontWeight: 600 }}>
										{showTrendSign ? (trendPositive ? "+" : "-") : ""}
										{trendValue}{trendSuffix} {effectiveTrendLabel}
									</Typography>
								</Stack>
							)
						}
						sx={{
							height: trendLabelBelow ? "auto" : 32,
							borderRadius: 2,
							px: 0.5,
							py: trendLabelBelow ? 0.25 : 0,
							mx: 0,
							bgcolor: isTimeTrend ? "#eff6ff" : trendPositive ? "#dcfce7" : "#ffe4e6",
							color: isTimeTrend ? "#1d4ed8" : trendPositive ? "#15803d" : "#be123c",
							"& .MuiChip-label": {
								px: 1,
								py: trendLabelBelow ? 0.25 : 0,
							},
						}}
					/>
				</Box>
			</Box>
		</Card>
	);
}
